import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Button, ButtonLink } from '@carouselgg/nexus/button'
import { ConfirmDialog } from '@carouselgg/nexus/confirm-dialog'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { Tab, TabList, TabPanel, Tabs } from '@carouselgg/nexus/tabs'
import { ArrowRight, Check, FileText, Ticket, Undo2 } from '@carouselgg/nexus/icon'
import { toast } from '@carouselgg/nexus/toast'
import { useAuthStore } from '@carouselgg/auth/store'
import {
  getMyActivity, withdrawApplication, withdrawSignup,
  type MyActivity, type MyApplication, type MyEntry, type MySignup,
} from '../../api/myActivity'
import { acceptInvite, declineInvite, getMyInvites, type MyInvite } from '../../api/invites'
import { answerTeamInvite, listMyTeamInvites, type MyTeamInvite } from '../../api/recruit'
import {
  applicationStatus, competitionPath, entryStatus, isLiveApplication, isLiveSignup,
  roleLabel, rulesOwed, shortDate, splitEntries,
} from './competitionsModel'
import styles from './CompetitionsPage.module.css'

type TabKey = 'entered' | 'applications' | 'past'
const TABS: TabKey[] = ['entered', 'applications', 'past']

/** Something the player can back out of, waiting on a confirm. */
type Withdrawing =
  | { kind: 'signup'; item: MySignup }
  | { kind: 'application'; item: MyApplication }

/**
 * Competitions in the account hub: everything the player's teams entered,
 * signed up for on their own, applied to or been invited to, in one place,
 * with what each one still owes and a way to back out of what they can.
 */
export function CompetitionsPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [params, setParams] = useSearchParams()
  const tab = (TABS.includes(params.get('tab') as TabKey) ? params.get('tab') : 'entered') as TabKey
  const [activity, setActivity] = useState<MyActivity | null>(null)
  const [orgInvites, setOrgInvites] = useState<MyInvite[]>([])
  const [teamInvites, setTeamInvites] = useState<MyTeamInvite[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [withdrawing, setWithdrawing] = useState<Withdrawing | null>(null)

  const load = useCallback(async () => {
    try {
      const [a, oi, ti] = await Promise.all([
        getMyActivity(),
        getMyInvites().catch(() => ({ invites: [] as MyInvite[] })),
        listMyTeamInvites().catch(() => ({ applications: [] as MyTeamInvite[] })),
      ])
      setActivity(a)
      setOrgInvites(oi.invites ?? [])
      setTeamInvites(ti.applications ?? [])
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your competitions')
    }
  }, [])

  useEffect(() => { if (isAuthenticated) void load() }, [isAuthenticated, load])

  async function act(key: string, run: () => Promise<unknown>, done: string) {
    setBusy(key)
    try {
      await run()
      toast.success(done)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  async function confirmWithdraw() {
    if (!withdrawing) return
    const w = withdrawing
    await act(`w-${w.item.id}`,
      () => (w.kind === 'signup' ? withdrawSignup(w.item) : withdrawApplication(w.item)),
      w.kind === 'signup' ? 'Signup withdrawn' : 'Application withdrawn')
    setWithdrawing(null)
  }

  const { live, past } = splitEntries(activity?.entries ?? [])
  const owed = rulesOwed(activity?.entries ?? [])
  const signups = (activity?.signups ?? []).filter(isLiveSignup)
  const applications = (activity?.applications ?? []).filter(isLiveApplication)
  const pastSignups = (activity?.signups ?? []).filter((s) => !isLiveSignup(s))
  const pastApplications = (activity?.applications ?? []).filter((a) => !isLiveApplication(a))
  const waitingCount = orgInvites.length + teamInvites.length + signups.length + applications.length

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>What you&apos;re in</span>
        <DisplayHeading as="h1" size="5xl" className={styles.flat}>Competitions</DisplayHeading>
        <p className={styles.lede}>
          Everything you&apos;ve entered with a team, signed up for on your own, applied to or been invited to.
          One place to see what&apos;s owed and back out of anything.
        </p>
      </header>

      {error && <p className={styles.error} role="alert">{error}</p>}
      {!error && !activity && <p className={styles.muted}>Loading…</p>}

      {activity && owed.map((e) => (
        <div key={e.registration_id} className={styles.owed}>
          <FileText size={18} aria-hidden="true" className={styles.owedIcon} />
          <span className={styles.owedText}>
            <strong>{e.team_name} hasn&apos;t agreed to {e.name}&apos;s rules, version {e.rules_to_agree}.</strong>
            <span className={styles.muted}> The league asks every team&apos;s captain to agree before they play.</span>
          </span>
          <ButtonLink to={competitionPath(e, 'rules')} size="sm">
            <Check size={14} aria-hidden="true" /> Read and agree
          </ButtonLink>
        </div>
      ))}

      {activity && (
        <Tabs value={tab} onChange={(v) => setParams(v === 'entered' ? {} : { tab: v }, { replace: true })}>
          <TabList>
            <Tab value="entered" badge={live.length || undefined}>Entered</Tab>
            <Tab value="applications" badge={waitingCount || undefined}>Applications &amp; invites</Tab>
            <Tab value="past">Past</Tab>
          </TabList>

          <TabPanel value="entered">
            <Section title="With your teams" action={<ButtonLink to="/compete" variant="secondary" size="sm">Find a competition</ButtonLink>}>
              {live.length === 0
                ? <li className={styles.empty}>Your teams aren&apos;t in anything right now. <Link to="/compete">Find a competition</Link>.</li>
                : live.map((e) => <EntryRow key={e.registration_id} entry={e} />)}
            </Section>
            {live.length > 0 && (
              <p className={styles.note}>To pull a team out of a competition that&apos;s started, open it and ask the league; they decide how it&apos;s scored.</p>
            )}
          </TabPanel>

          <TabPanel value="applications">
            <Section title="Invites to you" note="Answer them before they lapse">
              {orgInvites.length + teamInvites.length === 0
                ? <li className={styles.empty}>No invites waiting.</li>
                : (
                  <>
                    {teamInvites.map((i) => (
                      <Row
                        key={`t-${i.id}`}
                        title={<>{i.team_name ?? 'A team'} want you on their roster</>}
                        meta={[i.org_name, i.season_name, i.message && `“${i.message}”`].filter(Boolean).join(' · ')}
                        status={i.expires_at ? `Answer by ${shortDate(i.expires_at)}` : undefined}
                        actions={(
                          <>
                            <Button size="sm" disabled={busy !== null} onClick={() => act(`t-${i.id}`, () => answerTeamInvite(i.id, 'accepted'), `You joined ${i.team_name ?? 'the team'}`)}>
                              <Check size={14} aria-hidden="true" /> Accept
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => act(`t-${i.id}`, () => answerTeamInvite(i.id, 'declined'), 'Invite declined')}>
                              Decline
                            </Button>
                          </>
                        )}
                      />
                    ))}
                    {orgInvites.map((i) => (
                      <Row
                        key={`o-${i.id}`}
                        title={<>{i.organization.name} invited you to join as a {i.role}</>}
                        meta={i.organization.type === 'league' ? 'League' : 'Club'}
                        status={`Answer by ${shortDate(i.expires_at)}`}
                        actions={(
                          <>
                            <Button size="sm" disabled={busy !== null} onClick={() => act(`o-${i.id}`, () => acceptInvite(i.token), `You joined ${i.organization.name}`)}>
                              <Check size={14} aria-hidden="true" /> Accept
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => act(`o-${i.id}`, () => declineInvite(i.token), 'Invite declined')}>
                              Decline
                            </Button>
                          </>
                        )}
                      />
                    ))}
                  </>
                )}
            </Section>

            <Section title="Signed up solo" note="Free-agent pools captains pick from, and requests to join a club">
              {signups.length === 0
                ? <li className={styles.empty}>You&apos;re not in any free-agent pool.</li>
                : signups.map((s) => (
                  <Row
                    key={s.id}
                    title={s.season_name ? `${s.season_name} · free agents` : `Join ${s.organization_name}${s.team_name ? ` · ${s.team_name}` : ''}`}
                    meta={[s.organization_name, s.game_name].join(' · ')}
                    status={s.status === 'open' ? 'Listed · captains can invite you' : 'Waiting for the league'}
                    actions={(
                      <>
                        {s.season_slug && (
                          <ButtonLink to={competitionPath({ league_slug: s.organization_slug, slug: s.season_slug }, 'free-agents')} size="sm" variant="secondary">Edit</ButtonLink>
                        )}
                        <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setWithdrawing({ kind: 'signup', item: s })}>
                          <Undo2 size={14} aria-hidden="true" /> Withdraw
                        </Button>
                      </>
                    )}
                  />
                ))}
            </Section>

            <Section title="Applications you sent">
              {applications.length === 0
                ? <li className={styles.empty}>No applications waiting. Answered ones move to Past.</li>
                : applications.map((a) => (
                  <Row
                    key={a.id}
                    title={<>Asked to join {a.team_name}</>}
                    meta={[a.organization_name, a.season_name, a.message && `“${a.message}”`].filter(Boolean).join(' · ')}
                    status={`${applicationStatus(a.status)} · sent ${shortDate(a.created_at)}`}
                    actions={(
                      <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setWithdrawing({ kind: 'application', item: a })}>
                        <Undo2 size={14} aria-hidden="true" /> Withdraw
                      </Button>
                    )}
                  />
                ))}
            </Section>
          </TabPanel>

          <TabPanel value="past">
            <Section title="Finished and closed">
              {past.length + pastSignups.length + pastApplications.length === 0
                ? <li className={styles.empty}>Nothing finished yet. When a competition ends it moves here, and stays on your public profile.</li>
                : (
                  <>
                    {past.map((e) => <EntryRow key={e.registration_id} entry={e} />)}
                    {pastApplications.map((a) => (
                      <Row key={a.id} title={<>Asked to join {a.team_name}</>}
                        meta={[a.organization_name, a.season_name, a.review_note && `“${a.review_note}”`].filter(Boolean).join(' · ')}
                        status={applicationStatus(a.status)} />
                    ))}
                    {pastSignups.map((s) => (
                      <Row key={s.id} title={s.season_name ? `${s.season_name} · free agents` : `Join ${s.organization_name}`}
                        meta={[s.organization_name, s.game_name].join(' · ')} status={applicationStatus(s.status)} />
                    ))}
                  </>
                )}
            </Section>
          </TabPanel>
        </Tabs>
      )}

      <ConfirmDialog
        open={!!withdrawing}
        title={withdrawing?.kind === 'signup' ? 'Withdraw your signup?' : 'Withdraw your application?'}
        description={withdrawing?.kind === 'signup'
          ? `You'll leave ${withdrawing.item.season_name || withdrawing.item.organization_name}'s free agents. You can sign up again while it's open.`
          : withdrawing ? `${withdrawing.item.team_name} will no longer see your application.` : ''}
        confirmLabel={busy ? 'Withdrawing…' : 'Withdraw'}
        confirmIcon={<Undo2 size={16} aria-hidden="true" />}
        cancelLabel="Keep it"
        variant="danger"
        onConfirm={confirmWithdraw}
        onClose={() => setWithdrawing(null)}
      />
    </div>
  )
}

function EntryRow({ entry: e }: { entry: MyEntry }) {
  const status = entryStatus(e)
  return (
    <Row
      logo={e.game_logo_url}
      title={<Link to={competitionPath(e)} className={styles.name}>{e.name}</Link>}
      meta={`${e.team_name} · ${roleLabel(e.team_role)} · ${e.league_name}${e.in_person ? ' · in person' : ''}`}
      status={status.label}
      tone={status.tone}
      owes={e.rules_to_agree > 0 ? `Agree to rules v${e.rules_to_agree}` : undefined}
      actions={(
        <>
          {e.in_person && status.tone !== 'done' && (
            <ButtonLink to="/passes" size="sm" variant="secondary"><Ticket size={14} aria-hidden="true" /> Your pass</ButtonLink>
          )}
          <ButtonLink to={competitionPath(e)} size="sm" variant="ghost">Open <ArrowRight size={14} aria-hidden="true" /></ButtonLink>
        </>
      )}
    />
  )
}

function Row({ logo, title, meta, status, tone, owes, actions }: {
  logo?: string
  title: ReactNode
  meta?: string
  status?: string
  tone?: 'live' | 'waiting' | 'done'
  owes?: string
  actions?: ReactNode
}) {
  return (
    <li className={styles.row}>
      <span className={styles.logo} aria-hidden="true">{logo ? <img src={logo} alt="" /> : null}</span>
      <span className={styles.who}>
        <span className={styles.title}>{title}</span>
        {meta && <span className={styles.meta}>{meta}</span>}
      </span>
      <span className={styles.state}>
        {status && <span className={tone ? styles[tone] : undefined}>{status}</span>}
        {owes && <span className={styles.owes}>{owes}</span>}
      </span>
      <span className={styles.actions}>{actions}</span>
    </li>
  )
}

function Section({ title, note, action, children }: { title: string; note?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.sectionHead}>
        <DisplayHeading as="h2" size="3xl" className={styles.flat}>{title}</DisplayHeading>
        {note && <span className={styles.muted}>{note}</span>}
        {action && <span className={styles.sectionAction}>{action}</span>}
      </div>
      <ul className={styles.rows}>{children}</ul>
    </section>
  )
}

